<script lang="ts">
	// Full-screen image preview (SAY-1806). Mounted once in the layout; opened
	// through openLightbox() from any thumbnail. The image grows out of the
	// thumbnail's box (FLIP via the Web Animations API: laid out at its final
	// fitted rect, inverted onto the thumbnail's un-clipped cover rect, then
	// played to identity) and shrinks back on close. Esc, the backdrop, the
	// image itself or the × close it. Honors the app's reduced-motion switch
	// (`data-reduced-motion` on <html>) by skipping the animation.
	import XIcon from '@lucide/svelte/icons/x';
	import { closeLightbox, lightboxRequest } from '$lib/lightbox.svelte';
	import { coverRect, fitRect, flipTransform, type Rect, type Size } from '$lib/lightbox-geometry';

	const DURATION_MS = 220;
	const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

	const req = $derived(lightboxRequest());
	let imgEl = $state<HTMLImageElement | null>(null);
	let backdropEl = $state<HTMLDivElement | null>(null);
	// The fitted rect, known once the image's natural size is; null while loading.
	let target = $state<Rect | null>(null);
	let natural = $state<Size | null>(null);
	let closing = false;

	function reducedMotion(): boolean {
		return typeof document !== 'undefined' && document.documentElement.hasAttribute('data-reduced-motion');
	}

	// Measure the image for the request, then lay the stage out.
	$effect(() => {
		const r = req;
		target = null;
		natural = null;
		closing = false;
		if (!r) return;
		let cancelled = false;
		const probe = new Image();
		probe.onload = () => {
			if (cancelled) return;
			const size = { w: probe.naturalWidth || 1, h: probe.naturalHeight || 1 };
			natural = size;
			target = fitRect(size, { w: window.innerWidth, h: window.innerHeight });
		};
		probe.onerror = () => {
			if (cancelled) return;
			natural = { w: 1, h: 1 };
			target = fitRect({ w: 1, h: 1 }, { w: window.innerWidth, h: window.innerHeight });
		};
		probe.src = r.src;
		return () => {
			cancelled = true;
		};
	});

	// Grow out of the thumbnail once the stage exists.
	$effect(() => {
		const r = req;
		const to = target;
		const img = imgEl;
		const n = natural;
		if (!r || !to || !img || !n) return;
		if (!r.from || reducedMotion()) return;
		const from = coverRect(r.from, n);
		img.animate(
			[
				{ transform: flipTransform(from, to), borderRadius: '0.75rem' },
				{ transform: 'none', borderRadius: '0.5rem' },
			],
			{ duration: DURATION_MS, easing: EASING },
		);
		backdropEl?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: DURATION_MS, easing: 'ease-out' });
	});

	async function close(): Promise<void> {
		if (closing) return;
		closing = true;
		const r = req;
		const to = target;
		const img = imgEl;
		const n = natural;
		if (r?.from && to && img && n && !reducedMotion()) {
			const from = coverRect(r.from, n);
			const shrink = img.animate(
				[
					{ transform: 'none', borderRadius: '0.5rem' },
					{ transform: flipTransform(from, to), borderRadius: '0.75rem' },
				],
				{ duration: DURATION_MS, easing: EASING, fill: 'forwards' },
			);
			const fade = backdropEl?.animate([{ opacity: 1 }, { opacity: 0 }], {
				duration: DURATION_MS,
				easing: 'ease-in',
				fill: 'forwards',
			});
			await Promise.all([shrink.finished, fade?.finished].filter(Boolean)).catch(() => undefined);
		}
		closeLightbox();
	}

	// Keep the stage fitted when the window resizes while open.
	function onResize(): void {
		if (natural) target = fitRect(natural, { w: window.innerWidth, h: window.innerHeight });
	}
	function onKeydown(e: KeyboardEvent): void {
		if (e.key === 'Escape' && req) {
			e.preventDefault();
			void close();
		}
	}
</script>

<svelte:window onkeydown={onKeydown} onresize={onResize} />

{#if req && target}
	<div
		bind:this={backdropEl}
		class="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm"
		role="dialog"
		aria-modal="true"
		aria-label="Image preview"
		data-testid="image-lightbox"
		tabindex="-1"
		onclick={() => void close()}
		onkeydown={(e) => {
			if (e.key === 'Enter' || e.key === ' ') void close();
		}}
	>
		<button
			type="button"
			class="absolute top-3 right-3 z-10 flex size-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
			aria-label="Close preview"
			title="Close (Esc)"
			onclick={(e) => {
				e.stopPropagation();
				void close();
			}}
		>
			<XIcon class="size-5" strokeWidth={2} aria-hidden="true" />
		</button>
		<img
			bind:this={imgEl}
			src={req.src}
			alt={req.alt}
			class="fixed origin-top-left rounded-lg object-contain shadow-2xl select-none"
			style="left: {target.x}px; top: {target.y}px; width: {target.w}px; height: {target.h}px;"
			draggable="false"
		/>
	</div>
{/if}
